import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { formatResultsData } from '../utils';
import { resetSelectsOptions, resetVersions, formIsDirty } from '../actions';

const useFormActions = (yearOptions, setResult, dispatch, gtm) => {
  const [showResults, setShowResults] = useState();
  const [updatedSelect, setUpdatedSelect] = useState('');
  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
  } = useForm();

  // On load, register all of the select boxes with RHF
  useEffect(() => {
    register({ name: 'make' }, { required: true });
    register({ name: 'model' }, { required: true });
    register({ name: 'version' }, { required: true });
    register({ name: 'year' });

    // Set default value for 'year'
    setValue('year', yearOptions[0]);
  }, []);

  // On submit form
  const handleOnSubmit = (selectedVersion) => (formData) => {
    const { make, model, version, year } = formData;

    /* istanbul ignore else */
    if ((make, model, version, year)) {
      setResult(formatResultsData(selectedVersion, getValues('year').value));
      setShowResults(true);
      dispatch(formIsDirty(false));

      gtm &&
        gtm({
          action: 'Company car calculator',
          label: `${make.label}/${model.label}/${version.label}/${year.label}`,
        });
    }
  };

  // On reset form
  const handleResetForm = (e) => {
    const currentDate = new Date();
    const aprilFirst2024 = new Date('2024-04-01');
    let year = { value: '0', label: '2023/2024' };

    // Remove old year data after april 2024
    if (currentDate > aprilFirst2024) {
      year = { value: '0', label: '2024/2025' };
    }

    e.preventDefault();
    setValue('make', null);
    setValue('model', null);
    setValue('version', null);
    setValue('year', year);
    setShowResults(false);
    dispatch(resetSelectsOptions());
    dispatch(formIsDirty(false));
  };

  const updateFormAndDispatch = (selectNames, dispatchFunc) => {
    if (selectNames && selectNames.length) {
      selectNames.map((name) => {
        setValue(name, null);
        clearErrors(name);
      });
    }
    if (dispatchFunc) {
      dispatch(dispatchFunc());
    }
    dispatch(formIsDirty(true));
  };

  const handleChangeFuncs = {
    make: () => {
      updateFormAndDispatch(['model', 'version'], resetSelectsOptions);
    },
    model: () => {
      updateFormAndDispatch(['version'], resetVersions);
    },
    version: () => {
      updateFormAndDispatch();
    },
    year: () => {
      updateFormAndDispatch();
    },
  };

  // On handle change
  const handleChange = (newValue, { name }) => {
    if (getValues([name])?.value !== newValue.value) {
      handleChangeFuncs[name]();
    }

    // Remove error message when an option is selected
    if (errors[name] && newValue.value !== '') {
      errors[name] = null;
    }

    // We listen for an update to this 'updatedSelect'
    // inside 'useAPICarData' useEffect. It is a dependency
    // so that we can check when the form is changed and
    // then fire off the new query
    setUpdatedSelect({
      name,
      newValue: newValue.value,
    });

    // Set new values
    setValue(name, newValue);
  };

  return {
    handleSubmit,
    handleOnSubmit,
    handleChange,
    handleResetForm,
    getValues,
    errors,
    updatedSelect,
    showResults,
    setShowResults,
  };
};

export default useFormActions;
